import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Users,
  Activity,
  Eye,
  TrendingUp,
  Calendar,
  RefreshCw,
  AlertCircle,
  MousePointerClick,
  Globe,
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import { useSiteAnalytics, AnalyticsStats } from '../hooks/useSiteAnalytics';

export default function AdminPage() {
  const { t } = useTranslation();
  const { stats, loading, error, refresh } = useSiteAnalytics();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-16 h-16 text-[#EE1C23] mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('admin.title', 'Admin')}
              </h1>
            </div>
            <p className="text-gray-400 text-sm">
              {t('admin.analyticsDashboard', 'Návštěvnost webu v reálném čase')}
            </p>
          </motion.div>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-8 flex items-center justify-between gap-4 max-w-3xl mx-auto"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
              <button
                onClick={refresh}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-medium transition-all flex-shrink-0"
              >
                <RefreshCw className="w-4 h-4" />
                {t('common.retry', 'Retry')}
              </button>
            </motion.div>
          )}

          {loading && !stats ? (
            <div className="text-center py-20">
              <div className="inline-block w-10 h-10 border-4 border-red-500/30 border-t-[#EE1C23] rounded-full animate-spin mb-4" />
              <p className="text-gray-400 text-sm">{t('common.loading', 'Načítání…')}</p>
            </div>
          ) : stats ? (
            <AnalyticsDashboard stats={stats} onRefresh={refresh} loading={loading} />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function AnalyticsDashboard({
  stats,
  onRefresh,
  loading,
}: {
  stats: AnalyticsStats;
  onRefresh: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const todayVsYesterday = useMemo(() => {
    if (!stats.visitorsYesterday) return null;
    const diff = stats.visitorsToday - stats.visitorsYesterday;
    const pct = (diff / stats.visitorsYesterday) * 100;
    return { diff, pct };
  }, [stats.visitorsToday, stats.visitorsYesterday]);

  const maxHourly = useMemo(
    () => Math.max(1, ...stats.hourlyVisitors),
    [stats.hourlyVisitors]
  );

  return (
    <div className="space-y-6">
      {/* Top stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          label={t('admin.activeNow', 'Právě aktivních')}
          value={stats.activeNow}
          accent="green"
          pulse
          subtitle={t('admin.last5Minutes', 'za posledních 5 minut')}
        />
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label={t('admin.totalVisitors', 'Celkem návštěvníků')}
          value={stats.totalVisitors}
          accent="red"
          subtitle={t('admin.allTime', 'za celou dobu')}
        />
        <StatCard
          icon={<MousePointerClick className="w-5 h-5" />}
          label={t('admin.totalPageViews', 'Celkem zobrazení stránek')}
          value={stats.totalPageViews}
          accent="cyan"
          subtitle={t('admin.allTime', 'za celou dobu')}
        />
        <StatCard
          icon={<Eye className="w-5 h-5" />}
          label={t('admin.visitors24h', 'Návštěvníci 24h')}
          value={stats.visitors24h}
          accent="blue"
          subtitle={`${stats.pageViews24h} ${t('admin.pageViews', 'zobrazení')}`}
        />
      </div>

      {/* Refresh + Today vs Yesterday */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div>
            <div className="text-white font-medium text-sm">
              {t('admin.today', 'Dnes')}: <span className="text-[#EE1C23] font-bold">{stats.visitorsToday}</span>
              {todayVsYesterday && (
                <span
                  className={`ml-3 text-xs font-semibold px-2 py-0.5 rounded-md ${
                    todayVsYesterday.diff >= 0
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {todayVsYesterday.diff >= 0 ? '+' : ''}
                  {todayVsYesterday.pct.toFixed(1)}% {t('admin.vsYesterday', 'vs včera')}
                </span>
              )}
            </div>
            <div className="text-gray-500 text-xs mt-0.5">
              {t('admin.yesterday', 'Včera')}: {stats.visitorsYesterday}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-sm font-medium transition-all border border-white/10 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Aktualizovat')}
        </button>
      </div>

      {/* Hourly chart (sparkline bars) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#EE1C23]" />
          <h3 className="text-white font-semibold">
            {t('admin.visitorsLast24h', 'Návštěvnost za posledních 24 hodin')}
          </h3>
        </div>
        <div className="flex items-end justify-between gap-1 h-32">
          {stats.hourlyVisitors.map((count, hour) => {
            const heightPct = (count / maxHourly) * 100;
            return (
              <div
                key={hour}
                className="flex-1 flex flex-col items-center justify-end group relative"
                title={`Před ${23 - hour}h: ${count}`}
              >
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%` }}
                  transition={{ duration: 0.5, delay: hour * 0.02 }}
                  className="w-full rounded-t bg-gradient-to-t from-[#EE1C23]/40 to-[#EE1C23] group-hover:from-[#EE1C23] group-hover:to-red-400 transition-colors"
                  style={{ minHeight: count > 0 ? '4px' : '0' }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-gray-500 text-xs">
          <span>{t('admin.24hAgo', 'před 24h')}</span>
          <span>{t('admin.now', 'nyní')}</span>
        </div>
      </motion.div>

      {/* Top pages */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-[#EE1C23]" />
          <h3 className="text-white font-semibold">
            {t('admin.topPages24h', 'Nejnavštěvovanější stránky (24h)')}
          </h3>
        </div>
        {stats.topPages.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center">
            {t('admin.noDataYet', 'Zatím nejsou žádná data')}
          </p>
        ) : (
          <div className="space-y-2">
            {stats.topPages.map((page, idx) => {
              const maxCount = stats.topPages[0].count || 1;
              const pct = (page.count / maxCount) * 100;
              return (
                <div
                  key={page.path}
                  className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2"
                >
                  <span className="text-gray-500 text-xs font-mono w-6">#{idx + 1}</span>
                  <span className="text-gray-300 text-sm flex-1 truncate font-mono">{page.path}</span>
                  <div className="hidden sm:block w-32 bg-black/40 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#EE1C23] to-red-500 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[#EE1C23] font-semibold text-sm w-12 text-right">
                    {page.count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  accent,
  subtitle,
  pulse,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent: 'red' | 'green' | 'cyan' | 'blue';
  subtitle?: string;
  pulse?: boolean;
}) {
  const accentMap = {
    red: { text: 'text-[#EE1C23]', bg: 'bg-[#EE1C23]/20', border: 'border-[#EE1C23]/30' },
    green: { text: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30' },
    cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30' },
    blue: { text: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
  };
  const c = accentMap[accent];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-black/40 backdrop-blur-md border ${c.border} rounded-2xl p-5 relative overflow-hidden`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`${c.bg} rounded-lg p-2`}>{icon}</div>
        {pulse && (
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </span>
        )}
      </div>
      <div className={`text-3xl font-bold ${c.text}`}>{value.toLocaleString()}</div>
      <div className="text-gray-300 text-sm mt-1 font-medium">{label}</div>
      {subtitle && <div className="text-gray-500 text-xs mt-1">{subtitle}</div>}
    </motion.div>
  );
}
